import { createSelector } from '@ngrx/store';
import { selectUserDetails } from './user.selectors';
import { EventSystemComplete } from '../../models/event-systems/event-system-complete.model';
import { selectAllEventSystems } from './event-systems.selectors';
import { AppState } from '../app.state';
import { selectAppState } from './app.selector';
import { adminSystemAdapter } from '../adapters/admin/event-system.adapter';
import { adminEventAdapter } from '../adapters/admin/event.adapter';
import { adminHostAdapter } from '../adapters/admin/event-host.adapter';
import { adminOrganiserAdapter } from '../adapters/admin/organiser.adapter';

const selectAdminStore = createSelector(
    selectAppState,
    (state: AppState) => state.admin
)

const {
  selectIds: selectAdminEventIds,
  selectEntities: selectAdminEventEntities,
  selectAll: selectAllAdminEvents,
  selectTotal: selectAdminTotalEvents,
} = adminEventAdapter.getSelectors(createSelector(selectAdminStore, state => state.events));

const {
  selectIds: selectAdminSystemIds,
  selectEntities: selectAdminSystemEntities,
  selectAll: selectAllAdminSystems,
  selectTotal: selectAdminTotalSystems,
} = adminSystemAdapter.getSelectors(createSelector(selectAdminStore, state => state.eventsystems));

const {
  selectIds: selectAdminHostIds,
  selectEntities: selectAdminHostEntities,
  selectAll: selectAllAdminHosts,
  selectTotal: selectAdminTotalHosts,
} = adminHostAdapter.getSelectors(createSelector(selectAdminStore, state => state.eventHosts));

const {
  selectIds: selectAdminOrganiserIds,
  selectEntities: selectAdminOrganiserEntities,
  selectAll: selectAllAdminOrganisers,
  selectTotal: selectAdminTotalOrganisers,
} = adminOrganiserAdapter.getSelectors(createSelector(selectAdminStore, state => state.organisers));

export { selectAllAdminOrganisers }

export const selectAdminMenuVisibility = createSelector(
    selectAdminStore,
    state => state.menu.visible
)

export const selectAdminAllEvents = createSelector(
    selectAllAdminEvents,
    state => state.filter(x => new Date(x.eventEnd) >= new Date() && x.cancelled === false)
)

export const selectAdminArchiveEvents = createSelector(
    selectAllAdminEvents,
    state => state.filter(x => new Date(x.eventEnd) <= new Date() || x.cancelled === true)
)

export const selectAdminArchiveLoaded = createSelector(
    selectAdminStore,
    state => state.archiveLoaded
);

export const selectCurrentLoaded = createSelector(
    selectAdminStore,
    state => state.currentLoaded
);

export const selectAdminEventHosts = createSelector(
    selectAdminStore,
    state => state.eventHosts
)

export const selectAdminEventSystems = createSelector(
    selectAllAdminSystems,
    state => state
)

export const selectAdminPrivilegedUsers = createSelector(
    selectAdminStore,
    state => state.privilegedUsers
)

export const selectAdminPrivilegedUser = (id:number) => createSelector(
    selectAdminStore,
    state => state.privilegedUsers
)

export const selectAdminUsers = createSelector(
    selectAdminStore,
    state => state.users
)

export const selectAdminUser = (id:number) => createSelector(
    selectAdminStore,
    state => state.users[id]
)

export const selectAdminEventHost = (id:number) => createSelector(
    selectAllAdminHosts,
    state => state.find(x => x.hostID == id)
)

export const selectAdminEventSystem = (systemRef:string) => createSelector(
    selectAllAdminSystems,
    state => state.find(x => x.systemRef == systemRef)
)

export const selectAdminPermittedEventSystems = createSelector(
    selectAllAdminSystems,
    selectAllAdminHosts,
    selectUserDetails,
    (systems, hosts, user) => {
        const map = new Map<number, EventSystemComplete>();
        systems.filter(x => user?.eventSystems?.includes(x.systemID)).map(s => map.set(s.systemID,s as EventSystemComplete));
        hosts.filter(x => user?.eventHosts?.includes(x.hostID)).flatMap(h => h.eventSystem).forEach(s => {
            if (!map.has(s.systemID)) map.set(s.systemID, s as EventSystemComplete);
        });
        return Array.from(map.values()).sort((a,b) => a.name.localeCompare(b.name));
    }
)

export const selectAdminPermittedEventHosts = (systemID:number) => createSelector(
    selectAllAdminHosts,
    selectUserDetails,
    (hosts, user) => 
        hosts.filter(x => (user?.eventHosts?.includes(x.hostID) || user?.eventSystems?.includes(x.eventSystem.systemID)))
        .sort((a,b) => a.name.localeCompare(b.name)).filter(x => x.eventSystem.systemID == systemID)
);

export const selectAdminEvent = (eventRef:string | number) => createSelector(
    selectAdminAllEvents,
    state => state.find(x => x.eventLink === eventRef || x.eventID === eventRef)
)

export const selectEventBooking = (bookingId:number) => createSelector(
    selectAdminAllEvents,
    state => state.flatMap(x => x.eventBookings).find(x => x.bookingID == bookingId)
)

export const selectAdminAvailableTickets = (eventRef: string | number) => createSelector(
    selectAdminEvent(eventRef),
    event => {
      const now = new Date();
      return event?.eventTickets.filter(ticket =>
        new Date(ticket.availableFrom) <= now && new Date(ticket.availableTo) >= now
      ) || [];
    }
)


export const selectAdminUnavailableTickets = (eventRef: string | number) =>
  createSelector(
    selectAdminEvent(eventRef),
    event => {
      const now = new Date();
      return event?.eventTickets.filter(ticket => {
        const from = new Date(ticket.availableFrom);
        const to = new Date(ticket.availableTo);
        return from > now || to < now;
      }) || [];
    }
  );

export const selectAdminTicketTypes = createSelector(
    selectAdminStore,
    state => state.ticketTypes 
)

export const selectAdminOrganiser = (organiserID:number) => createSelector(
    selectAdminStore,
    state => state.organisers.entities[organiserID]
)

export const selectOrganiserVerified = (organiserID:number) => createSelector(
    selectAdminStore,
    state => {
        const lookup = state.organisers.entities[organiserID]?.paymentProvider?.some(x => x.verified);
        return lookup
    } 
)